import React, { useRef, useState } from "react";
import { Provider } from "react-redux";
import { END } from "redux-saga";

import allSagasDone from "./dynamic/allSagasDone";
import {
  AppContextWithModules,
  ModuleTuple,
  WrapperProps
} from "./types/contracts";
import hydrateAction from "./dynamic/hydrateAction";
import { ModuleStoreWithSagaTasks } from "./dynamic/sagaExtention/contracts";
import getStore from "./dynamic/storeInitializer";

interface InitialProps {
  [props: string]: any;
}

export default function withRedux(App: any, rootModules: ModuleTuple) {
  let initialStore: ModuleStoreWithSagaTasks;

  const makeProps = async (
    context: AppContextWithModules
  ): Promise<WrapperProps> => {
    initialStore = getStore({
      rootModules,
      pageModules: context.Component.modules,
      context
    });

    let initialProps: InitialProps = {};

    if (App.getInitialProps) {
      initialProps = await App.getInitialProps({
        ctx: {
          ...context.ctx,
          store: initialStore
        },
        Component: context.Component,
        store: initialStore
      });
    }

    if (context.ctx?.req) {
      initialStore.dispatch(END);
      await allSagasDone(
        initialStore.sagaTasks.keys.map(key => initialStore.sagaTasks.get(key))
      );
    }

    return {
      initialProps,
      initialState: initialStore.getState()
    };
  };

  const Wrapper = ({
    initialState,
    initialProps,
    Component,
    ...props
  }: WrapperProps & AppContextWithModules) => {
    const isFirstRender = useRef(true);
    // see https://reactjs.org/docs/hooks-faq.html#how-to-create-expensive-objects-lazily
    const [store] = useState(() => {
      if (typeof window === "undefined" && initialStore) {
        return initialStore;
      }

      return getStore({ rootModules, pageModules: Component.modules });
    });

    if (isFirstRender.current) {
      isFirstRender.current = false;

      if (initialState) {
        store.dispatch(hydrateAction(initialState));
      }
    }

    return (
      <Provider store={store}>
        <App {...initialProps} {...props} store={store} Component={Component} />
      </Provider>
    );
  };

  Wrapper.displayName = "withRedux(WrappedApp)";

  Wrapper.getInitialProps = async (
    context: AppContextWithModules
  ): Promise<WrapperProps> => {
    const props = await makeProps(context);
    return props;
  };

  return Wrapper;
}
